import React, { useMemo, MouseEvent } from 'react';
import { State } from 'store/types';
import { useStoreon } from 'storeon/react';
import { createElementKey } from 'utils';
import 'swiper/css/pagination';
import { MODEL_JSON_STORE_NAME, selectSeoProperties } from 'modules/model-json';
import { COLLECTIONS_STORE_NAME, Collection } from 'modules/collections';
import { selectAvailableCollections } from 'modules/collections/collections-selectors';
import {
  Link,
  PriceMediaCard,
  PriceMediaCardContent,
  PriceMediaCardDescription,
  PriceMediaCardSwiper,
  PriceMediaCardTitle,
  PriceView,
  Section,
  SectionTitle,
  Typography,
} from 'common/components';
import { PromoBlock } from 'remotes/promo-block';
import { analyticsUtils } from 'utils/analytics-utils';
import { KITCHEN_COLLECTIONS_CDN_PATH } from '../../kitchen-collections-constants';
import {
  collectionListSection,
  collectionListTitle,
  collectionListSubtitle,
  collectionsGrid,
  collectionButton,
  collectionButtonDisabled,
  collectionContentLink,
  collectionPriceView,
  collectionPriceWrapper,
} from './collections-list-styles';
import {
  getCheapestCollectionVariant,
  getCollectionTypeRange,
  getCollectionSizeRange,
  getHighestCollectionVariant,
  getTotalAvailablePoints,
} from './collections-list-utils';

type CollectionsListProps = {
  isCorner: boolean;
};

const CORNER_TYPE_ID = '10002';

export const CollectionsList: React.FC<CollectionsListProps> = ({ isCorner }) => {
  const { modelJson } = useStoreon<State>(MODEL_JSON_STORE_NAME);
  const { collections } = useStoreon<State>(COLLECTIONS_STORE_NAME);
  const seoProperties = useMemo(() => selectSeoProperties(modelJson), [modelJson]);

  const availableCollections = useMemo(() => selectAvailableCollections(collections), [
    collections,
  ]) as Collection[];

  const typeId = useMemo(() => (isCorner ? CORNER_TYPE_ID : undefined), [isCorner]);

  // Массив картинок в зависимости от угловой/прямой кухонь
  const getCollectionMediaList = (collection: Collection) =>
    isCorner ? [...collection.mediaPhoto].reverse() : collection.mediaPhoto;

  const handleCollectionLinkClick = (collection: Collection, position: number) => {
    analyticsUtils.clickButtonCollectionEvent(
      'komplekty / catalogue',
      collection.name,
      'Подробнее',
      position.toString(),
      getCheapestCollectionVariant(collection, typeId).productId
    );
  };

  const handleCollectionWheelClick = (e: MouseEvent, collection: Collection, position: number) => {
    if (e.button === 1) {
      handleCollectionLinkClick(collection, position);
    }
  };

  const lowestCollectionsPrice = useMemo(() => {
    const lowestPrices: number[] = [];
    availableCollections?.forEach(collection => {
      const cheapestVariant = getCheapestCollectionVariant(collection, typeId);
      if (cheapestVariant) {
        lowestPrices.push(cheapestVariant.price.price);
      }
    });
    return Math.min(...lowestPrices);
  }, [availableCollections, typeId]);

  const highestCollectionsPrice = useMemo(() => {
    const highestPrices: number[] = [];
    availableCollections?.forEach(collection => {
      const highestVariant = getHighestCollectionVariant(collection, typeId);
      if (highestVariant) {
        highestPrices.push(highestVariant.price.price);
      }
    });
    return Math.max(...highestPrices);
  }, [availableCollections, typeId]);

  return (
    <Section className={collectionListSection}>
      <SectionTitle as="h1" className={collectionListTitle}>
        {isCorner ? 'Угловые кухонные гарнитуры' : 'Готовые комплекты кухонь'}
      </SectionTitle>
      <Typography className={collectionListSubtitle} variant="l" size="body_long" adaptive={false}>
        {isCorner ? 'Готовые комплекты по\u00A0выгодным ценам' : 'Кухонные гарнитуры по\u00A0выгодным ценам'}
      </Typography>
      <PromoBlock elementBlock="komplekty / catalogue" />
      <ul className={collectionsGrid}>
        {availableCollections?.map((collection, index: number) => {
          const cheapestCollection = getCheapestCollectionVariant(collection, typeId);

          return (
            <li key={createElementKey('collection', index)}>
              <PriceMediaCard>
                <PriceMediaCardSwiper
                  keyName="collection-photo"
                  landingCdn={KITCHEN_COLLECTIONS_CDN_PATH}
                  photosName={collection.name}
                  photosSrc={getCollectionMediaList(collection)}
                />
                {getTotalAvailablePoints(collection, typeId).length > 0 ? (
                  <Link
                    onClick={() => handleCollectionLinkClick(collection, index + 1)}
                    onAuxClick={(e: MouseEvent) => handleCollectionWheelClick(e, collection, index + 1)}
                    href={getCheapestCollectionVariant(collection, typeId).productUrl}
                    title="Подробнее о гарнитуре"
                    className={collectionContentLink}>
                    <PriceMediaCardContent>
                      <div className={collectionPriceWrapper}>
                        <PriceMediaCardTitle as="h2">{collection.name}</PriceMediaCardTitle>
                        <PriceMediaCardDescription
                          description={`${getCollectionTypeRange(collection)},${getCollectionSizeRange(
                            collection
                          )}`}
                        />
                        <PriceView className={collectionPriceView} {...cheapestCollection.price} prefix='от' size="s" />
                      </div>
                      <Typography variant="s" size="body_accent" className={collectionButton}>
                        Подробнее
                      </Typography>
                    </PriceMediaCardContent>
                  </Link>
                ) : (
                  <PriceMediaCardContent>
                    <PriceMediaCardTitle as="h2">{collection.name}</PriceMediaCardTitle>
                    <Typography variant="s" size="body_accent" className={collectionButtonDisabled}>
                      Нет в&nbsp;наличии
                    </Typography>
                  </PriceMediaCardContent>
                )}
              </PriceMediaCard>
            </li>
          )
        })}
      </ul>
      <div itemScope itemType="https://schema.org/Product">
        <meta itemProp="name" content={seoProperties?.h1 ?? undefined} />
        <meta itemProp="description" content={seoProperties?.metadescription ?? undefined} />
        <div itemProp="offers" itemScope itemType="https://schema.org/AggregateOffer">
          <meta itemProp="lowPrice" content={lowestCollectionsPrice.toString()} />
          <meta itemProp="highPrice" content={highestCollectionsPrice.toString()} />
          <meta itemProp="offerCount" content={availableCollections?.length.toString() ?? undefined} />
          <meta itemProp="priceCurrency" content="RUB" />
        </div>
      </div>
    </Section >
  );
};
